import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { writeFetch } from '../../api';
import { getCached, setCached } from '../../utils/apiCache';

const {
  FiZoomIn, FiZoomOut, FiX, FiChevronLeft, FiChevronRight,
  FiMaximize2, FiRotateCcw, FiImage,
} = FiIcons;

const TABLE = 'product_images_1776542000';
const CACHE_TTL = 300000;
const pendingGalleryRequests = new Map();

/* ── Responsive image helpers ───────────────────────────────────────────────── */
const buildSrcSet = (url) => {
  if (!url || !url.includes('unsplash.com')) return undefined;
  const w400 = url.replace(/([?&])w=\d+/, '$1w=400');
  const w800 = url.replace(/([?&])w=\d+/, '$1w=800');
  const w1200 = url.replace(/([?&])w=\d+/, '$1w=1200');
  return `${w400} 400w, ${w800} 800w, ${w1200} 1200w`;
};

// ── Lightbox (zoom + navigate) ─────────────────────────────────────────────
const Lightbox = ({ images, startIndex, onClose }) => {
  const [idx, setIdx]       = useState(startIndex);
  const [zoom, setZoom]     = useState(1);
  const [pan, setPan]       = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);

  const img = images[idx];

  const prev = (e) => { e?.stopPropagation(); setIdx(i => (i - 1 + images.length) % images.length); resetZoom(); };
  const next = (e) => { e?.stopPropagation(); setIdx(i => (i + 1) % images.length); resetZoom(); };
  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const handleKey = useCallback((e) => {
    if (e.key === 'ArrowLeft')  prev();
    if (e.key === 'ArrowRight') next();
    if (e.key === 'Escape')     onClose();
    if (e.key === '+')          setZoom(z => Math.min(z + 0.5, 4));
    if (e.key === '-')          setZoom(z => Math.max(z - 0.5, 0.5));
  }, [idx]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    setDragging(true);
    dragStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };
  const onMouseMove = (e) => {
    if (!dragging || !dragStart.current) return;
    setPan({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
  };
  const onMouseUp = () => { setDragging(false); dragStart.current = null; };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[500] bg-black/95 backdrop-blur-sm flex flex-col"
      onClick={onClose}>

      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 text-white/60 text-sm font-light">
          <span className="font-bold text-white">{idx + 1}</span> / {images.length}
          {img?.alt_text && <span className="ml-3 text-white/50 italic truncate max-w-[200px]">{img.alt_text}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setZoom(z => Math.max(z - 0.5, 0.5))}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-all">
            <SafeIcon icon={FiZoomOut} className="w-4 h-4" />
          </button>
          <span className="text-white/60 text-xs w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(z + 0.5, 4))}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-all">
            <SafeIcon icon={FiZoomIn} className="w-4 h-4" />
          </button>
          <button onClick={resetZoom}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-all">
            <SafeIcon icon={FiRotateCcw} className="w-4 h-4" />
          </button>
          <button onClick={onClose}
            className="p-2 bg-white/10 hover:bg-red-500/80 rounded-xl text-white transition-all ml-2">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main image */}
      <div className="flex-1 flex items-center justify-center overflow-hidden relative"
        onClick={e => e.stopPropagation()}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
        style={{ cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in' }}>
        <AnimatePresence mode="wait">
          <motion.img key={idx}
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.2 }}
            src={img?.image_url || img}
            alt={img?.alt_text || `Image ${idx + 1}`}
            draggable={false}
            style={{
              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              transition: dragging ? 'none' : 'transform 0.2s ease',
              maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain',
            }}
            className="rounded-xl shadow-2xl select-none"
          />
        </AnimatePresence>

        {/* Nav arrows */}
        {images.length > 1 && (
          <>
            <button onClick={prev}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/80 rounded-2xl text-white transition-all border border-white/10">
              <SafeIcon icon={FiChevronLeft} className="w-6 h-6" />
            </button>
            <button onClick={next}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/80 rounded-2xl text-white transition-all border border-white/10">
              <SafeIcon icon={FiChevronRight} className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="flex gap-2 justify-center px-6 py-4 flex-shrink-0 overflow-x-auto" onClick={e => e.stopPropagation()}>
          {images.map((img, i) => (
            <button key={i} onClick={() => { setIdx(i); resetZoom(); }}
              className={`flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden border-2 transition-all ${
                i === idx ? 'border-white scale-110' : 'border-white/20 hover:border-white/50 opacity-60 hover:opacity-100'
              }`}>
              <img src={img?.image_url || img} alt={`thumb-${i}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
};

// ── Public Gallery (customer-facing) ──────────────────────────────────────
export const ProductImageGallery = ({ product, extraImages = [] }) => {
  const [images, setImages]     = useState([]);
  const [active, setActive]     = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      const cacheKey = `gallery_${product.id}`;
      const cached = getCached(cacheKey, CACHE_TTL);
      if (cached) {
        setImages(cached);
        setLoading(false);
        return;
      }

      const pending = pendingGalleryRequests.get(cacheKey);
      if (pending) {
        try {
          const all = await pending;
          setImages(all);
        } catch {
          const fallback = [{ id: 'fallback', image_url: product.image || product.image_url, alt_text: product.name }];
          setImages(fallback);
        }
        setLoading(false);
        return;
      }

      const request = (async () => {
        const res  = await writeFetch(`read/${TABLE}?product_id=${product.id}&sort=sort_order&order=asc&limit=50`);
        const data = await res.json();
        const dbImages = data.status === 'success' ? (data.data || []) : [];
        const all = dbImages.length > 0 ? dbImages : [
          { id: 'fallback', image_url: product.image || product.image_url, alt_text: product.name },
          ...extraImages.map((url, i) => ({ id: `extra-${i}`, image_url: url, alt_text: `${product.name} ${i + 2}` })),
        ];
        setCached(cacheKey, all);
        return all;
      })();

      pendingGalleryRequests.set(cacheKey, request);

      try {
        const all = await request;
        setImages(all);
      } catch {
        const fallback = [{ id: 'fallback', image_url: product.image || product.image_url, alt_text: product.name }];
        setImages(fallback);
        setCached(cacheKey, fallback);
      } finally {
        if (pendingGalleryRequests.get(cacheKey) === request) {
          pendingGalleryRequests.delete(cacheKey);
        }
        setLoading(false);
      }
    };
    if (product?.id) load();
  }, [product?.id]);

  if (loading) return (
    <div className="aspect-square bg-brand-light rounded-[40px] animate-pulse" />
  );

  const current = images[active] || images[0];

  return (
    <>
      {/* Main viewer */}
      <div className="relative rounded-[40px] overflow-hidden border border-brand-dark shadow-2xl aspect-square bg-white group">
        <AnimatePresence mode="wait">
          <motion.img key={active}
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            src={current?.image_url}
            srcSet={buildSrcSet(current?.image_url)}
            sizes="(max-width: 1024px) 100vw, 50vw"
            alt={current?.alt_text || product.name}
            loading="eager"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </AnimatePresence>

        {/* Zoom button */}
        <button onClick={() => setLightbox(active)}
          className="absolute top-4 right-4 p-3 bg-black/40 hover:bg-black/70 rounded-2xl text-white opacity-0 group-hover:opacity-100 transition-all backdrop-blur-sm">
          <SafeIcon icon={FiMaximize2} className="w-4 h-4" />
        </button>

        {/* Image count */}
        {images.length > 1 && (
          <div className="absolute bottom-4 right-4 bg-black/50 text-white text-[10px] font-black px-2.5 py-1 rounded-full backdrop-blur-sm">
            {active + 1} / {images.length}
          </div>
        )}

        {/* Nav arrows on main image */}
        {images.length > 1 && (
          <>
            <button onClick={() => setActive(a => (a - 1 + images.length) % images.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2.5 bg-black/40 hover:bg-black/70 rounded-xl text-white opacity-0 group-hover:opacity-100 transition-all backdrop-blur-sm">
              <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
            </button>
            <button onClick={() => setActive(a => (a + 1) % images.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2.5 bg-black/40 hover:bg-black/70 rounded-xl text-white opacity-0 group-hover:opacity-100 transition-all backdrop-blur-sm">
              <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="flex gap-2 mt-4 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <button key={img.id || i} onClick={() => setActive(i)}
              className={`flex-shrink-0 w-16 h-16 rounded-2xl overflow-hidden border-2 transition-all ${
                i === active
                  ? 'border-accent-gold scale-105 shadow-lg'
                  : 'border-brand-dark hover:border-accent-gold/50 opacity-70 hover:opacity-100'
              }`}>
              <img
                src={img.image_url}
                srcSet={buildSrcSet(img.image_url)}
                sizes="64px"
                alt={img.alt_text || `Image ${i + 1}`}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      <AnimatePresence>
        {lightbox !== null && (
          <Lightbox images={images} startIndex={lightbox} onClose={() => setLightbox(null)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default ProductImageGallery;